/*
 *
 * For cmyk values parseFloat(parseFloat(number),toFixed(2)) is used as
 * toFixed() returns a string value
 *
 */

import { useEffect, useState } from 'react';
import { utils as commonUtils } from '@sitebuilder/common';
import { CheckboxSwitch, TextInput } from '../index';

interface ColorSelectProps {
    value: string;
    hexColor: string;
    rgbColor: string;
    setHexColor: (value: any) => void;
    setRgbColor: (value: any) => void;
    onChange: (value: any) => void;
}

const ColorSelect = (props: ColorSelectProps) => {
    const { value, hexColor, rgbColor, setHexColor, setRgbColor, onChange } = props;
    const [autoConvert, setAutoConvert] = useState<boolean>(false);
    const [cmykColor, setCmykColor] = useState('');
    const [cmykC, setC] = useState(0);
    const [cmykM, setM] = useState(0);
    const [cmykY, setY] = useState(0);
    const [cmykK, setK] = useState(0);
    const [spot, setSpot] = useState('');

    useEffect(() => {
        if (value) {
            if (commonUtils.isJSON(value)) {
                const objValues = JSON.parse(value);
                setRgbColor(objValues.rgb === 'null' ? '' : objValues.rgb);
                if (objValues.hex) {
                    setHexColor(objValues.hex === 'null' ? '' : objValues.hex);
                } else if (objValues.rgb) {
                    setHexColor(commonUtils.RGBToHex(objValues.rgb) || '');
                }

                if (objValues.cmyk && objValues.cmyk !== 'null') {
                    const cmyk = objValues.cmyk.replace('cmyk(', '').replace(')', '');
                    const cmykArray = cmyk.split(',');
                    setC(parseFloat(parseFloat(cmykArray[0]).toFixed(2)));
                    setM(parseFloat(parseFloat(cmykArray[1]).toFixed(2)));
                    setY(parseFloat(parseFloat(cmykArray[2]).toFixed(2)));
                    setK(parseFloat(parseFloat(cmykArray[3]).toFixed(2)));
                    setCmykColor(objValues.cmyk === 'null' ? '' : objValues.cmyk);
                }

                setSpot(objValues.spot === 'null' ? '' : objValues.spot);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    // converts hex to rgb
    const hexToRgb = (hex: string) => {
        let r = 0;
        let g = 0;
        let b = 0;
        r = Number(`0x${hex[1]}${hex[2]}`);
        g = Number(`0x${hex[3]}${hex[4]}`);
        b = Number(`0x${hex[5]}${hex[6]}`);
        return `rgb(${r},${g},${b})`;
    };

    const convertCmyk = (hex: string) => {
        let computedC, computedM, computedY, computedK, minCMY, cmyk;
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);

        // Black
        if (r === 0 && g === 0 && b === 0) {
            cmyk = 'cmyk(0%, 0%, 0%, 100%)';
        } else {
            computedC = 1 - r / 255;
            computedM = 1 - g / 255;
            computedY = 1 - b / 255;
            minCMY = Math.min(computedC, Math.min(computedM, computedY));
            computedC = (computedC - minCMY) / (1 - minCMY);
            computedM = (computedM - minCMY) / (1 - minCMY);
            computedY = (computedY - minCMY) / (1 - minCMY);
            computedK = minCMY;
            computedC = 1 - r / 255;
            computedM = 1 - g / 255;
            computedY = 1 - b / 255;
            cmyk = `cmyk(${computedC * 100}%,${computedM * 100}%,${computedY * 100}%,${
                computedK * 100
            }%)`;
        }

        return cmyk;
    };

    const handleHexChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const hex = e.target.value.substring(1, 7);
        let cmyk = '';
        let newRgbColor = '';
        if (e.target.value !== '') {
            cmyk = convertCmyk(hex);
            newRgbColor = hexToRgb(e.target.value);
        }

        setHexColor(e.target.value);
        setRgbColor(newRgbColor);

        if (autoConvert) {
            setCmykColor(cmyk);
            const cmykArray = cmyk.replace('cmyk(', '').replace(')', '').split(',');
            setC(parseFloat(parseFloat(cmykArray[0]).toFixed(2)));
            setM(parseFloat(parseFloat(cmykArray[1]).toFixed(2)));
            setY(parseFloat(parseFloat(cmykArray[2]).toFixed(2)));
            setK(parseFloat(parseFloat(cmykArray[3]).toFixed(2)));
            onChange(
                `{"hex":"${e.target.value === '' ? null : e.target.value}", "rgb":"${
                    newRgbColor === '' ? null : newRgbColor
                }", "cmyk":"${cmyk === '' ? null : cmyk}", "spot":"${spot === '' ? null : spot}"}`
            );
        } else {
            onChange(
                `{"hex":"${e.target.value === '' ? null : e.target.value}", "rgb":"${
                    newRgbColor === '' ? null : newRgbColor
                }", "cmyk":"${cmykColor === '' ? null : cmykColor}", "spot":"${
                    spot === '' ? null : spot
                }"}`
            );
        }
    };

    // converts cmyk color to rgb
    const cmykToRgb = (c: Number, m: Number, y: Number, k: Number) => {
        let rgbR;
        let rgbG;
        let rgbB;
        let cyan = 100 * Number(c);
        let magenta = 100 * Number(m);
        let yellow = 100 * Number(y);
        let black = 100 * Number(k);
        cyan > 0 && (cyan /= 100);
        magenta > 0 && (magenta /= 100);
        yellow > 0 && (yellow /= 100);
        black > 0 && (black /= 100);
        rgbR = 1 - Math.min(1, cyan * (1 - black) + black);
        rgbG = 1 - Math.min(1, magenta * (1 - black) + black);
        rgbB = 1 - Math.min(1, yellow * (1 - black) + black);
        rgbR = Math.round(255 * rgbR);
        rgbG = Math.round(255 * rgbG);
        rgbB = Math.round(255 * rgbB);
        return { r: rgbR, g: rgbG, b: rgbB };
    };

    const componentToHex = (c: Number) => {
        const hex = c.toString(16);
        return hex.length === 1 ? `0${hex}` : hex;
    };

    // converts rgb to hex
    const rgbToHex = (r: Number, g: Number, b: Number) => {
        const hexString = `#${componentToHex(r)}${componentToHex(g)}${componentToHex(b)}`.substring(
            0,
            7
        );
        return hexString;
    };

    const convertHex = (cmyk: string) => {
        cmyk = cmyk.replace('cmyk(', '').replace(')', '');
        const cmykArray = cmyk.split(',');
        const c = parseInt(cmykArray[0]);
        const m = parseInt(cmykArray[1]);
        const y = parseInt(cmykArray[2]);
        const k = parseInt(cmykArray[3]);
        // convert cmyk to rgb first
        const rgb = cmykToRgb(c, m, y, k);
        // then convert rgb to hex
        const hex = rgbToHex(rgb.r, rgb.g, rgb.b);
        // return hex color format
        return hex;
    };

    const handleCmykChange = (cmyk: string) => {
        setCmykColor(cmyk);

        let newHex = '';
        let newRgbColor = '';

        if (cmyk !== '') {
            newHex = convertHex(cmyk);
            newRgbColor = hexToRgb(newHex);
        }

        if (autoConvert) {
            setHexColor(newHex);
            setRgbColor(newRgbColor);
            onChange(
                `{"hex":"${newHex === '' ? null : newHex}", "rgb":"${
                    newRgbColor === '' ? null : newRgbColor
                }", "cmyk":"${cmyk === '' ? null : cmyk}", "spot":"${spot === '' ? null : spot}"}`
            );
        } else {
            onChange(
                `{"hex":"${hexColor === '' ? null : hexColor}", "rgb":"${
                    rgbColor === '' ? null : rgbColor
                }", "cmyk":"${cmyk === '' ? null : cmyk}", "spot":"${spot === '' ? null : spot}"}`
            );
        }
    };

    const handleCmykCChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value: number = parseFloat(parseFloat(e.target.value).toFixed(2));
        setC(value);
        handleCmykChange(`cmyk(${value}%,${cmykM}%,${cmykY}%,${cmykK}%)`);
    };

    const handleCmykMChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value: number = parseFloat(parseFloat(e.target.value).toFixed(2));
        setM(value);
        handleCmykChange(`cmyk(${cmykC}%,${value}%,${cmykY}%,${cmykK}%)`);
    };

    const handleCmykYChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value: number = parseFloat(parseFloat(e.target.value).toFixed(2));
        setY(value);
        handleCmykChange(`cmyk(${cmykC}%,${cmykM}%,${value}%,${cmykK}%)`);
    };

    const handleCmykKChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value: number = parseFloat(parseFloat(e.target.value).toFixed(2));
        setK(value);
        handleCmykChange(`cmyk(${cmykC}%,${cmykM}%,${cmykY}%,${value}%)`);
    };

    const handleSpotChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSpot(e.target.value);
        onChange(
            `{"hex":"${hexColor === '' ? null : hexColor}", "rgb":"${
                rgbColor === '' ? null : rgbColor
            }", "cmyk":"${cmykColor === '' ? null : cmykColor}", "spot":"${
                e.target.value === '' ? null : e.target.value
            }"}`
        );
    };

    return (
        <div className="color-select">
            <CheckboxSwitch
                title="Auto Convert"
                checked={autoConvert}
                onChange={(e: any) => setAutoConvert(e.target.checked)}
            />
            <label>RGB / Hex</label>
            <input
                type="color"
                className="color-picker-show"
                value={hexColor}
                onChange={handleHexChange}
            />
            <div className="base-inputs">
                <label id="color-value-hex">Hex: {hexColor}</label>
                <label id="color-value-rgb">RGB: {rgbColor}</label>
                <div className="cmyk-picker-inputs">
                    <label>C</label>
                    <input
                        type="range"
                        min="0"
                        max="100"
                        onChange={handleCmykCChange}
                        value={cmykC}
                        className={'cymk-colors-c'}
                    />
                    <input id="c-value" type="number" value={cmykC} onChange={handleCmykCChange} />
                    <label>M</label>
                    <input
                        type="range"
                        min="0"
                        max="100"
                        onChange={handleCmykMChange}
                        value={cmykM}
                        className={'cymk-colors-m'}
                    />
                    <input id="m-value" type="number" value={cmykM} onChange={handleCmykMChange} />
                    <label>Y</label>
                    <input
                        type="range"
                        min="0"
                        max="100"
                        onChange={handleCmykYChange}
                        value={cmykY}
                        className={'cymk-colors-y'}
                    />
                    <input id="y-value" type="number" value={cmykY} onChange={handleCmykYChange} />
                    <label>K</label>
                    <input
                        type="range"
                        min="0"
                        max="100"
                        onChange={handleCmykKChange}
                        value={cmykK}
                        className={'cymk-colors-k'}
                    />
                    <input id="k-value" type="number" value={cmykK} onChange={handleCmykKChange} />
                </div>
                <TextInput
                    label="Spot"
                    value={spot}
                    onChange={handleSpotChange}
                    inputId={'color-value-spot'}
                />
            </div>
        </div>
    );
};

export default ColorSelect;
